import React, { useEffect, useMemo, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Button } from 'react-bootstrap';
import { ChatList } from 'react-chat-elements';
import ChatHeader from './ChatHeader';
import { CURRENT_VIEW, enrichUsersWithAvatarUrls } from './constant';
import ChatFooter from './ChatFooter';
import { LOCAL_STORAGE } from '../../../constants';
import { getLocalStorageItem, popup } from '../../../utils';
import { getAllChatsAPI } from '../../../utils/apiServices/chatsAPI';
import {
    initializeChatList,
    setCurrentView,
    setSelectedUser,
    toggleChatOpen,
} from '../../../redux';
import { ChatMessageBody } from './ChatMessageBody';
import { NoDataFound } from '../NoDataFound';
import { useDebounce } from '../../../hooks';
import { NewChatView } from './NewChatView';
import { ChatSearch } from './ChatSearch';
import { ChatIcon } from '../../../assets/svg';

export const FloatingChatElement = () => {
    const dispatch = useDispatch();
    const chatList = useSelector((s) => s.chat.chatList);
    const csmCaseloadData = useSelector((s) => s.csmCaseload?.csmCaseloadData);
    const chatNotifications = useSelector((s) => s.chat.chatNotifications);
    const isChatOpen = useSelector((s) => s.chat.isChatOpen);
    const currentView = useSelector((s) => s.chat.currentView);
    const selectedUser = useSelector((s) => s.chat.selectedUser);
    const isCsmCaseload = useSelector((s) => s.csmCaseload?.isCsmCaseload);
    const userId = useMemo(
        () => csmCaseloadData?.id || getLocalStorageItem(LOCAL_STORAGE.USER_ID),
        [csmCaseloadData],
    );

    const [search, setSearch] = useState('');
    const [chatSearch, setChatSearch] = useState('');
    const [messageSearch, setMessageSearch] = useState('');

    const getAllChats = useDebounce(async () => {
        try {
            const { data } = await getAllChatsAPI({
                management_id: userId,
                offset: 1,
                limit: 150,
                search,
            });
            const users = await enrichUsersWithAvatarUrls({ data });
            dispatch(initializeChatList(users));
        } catch (error) {
            popup('error', error.message || 'Failed to fetch chats');
        }
    }, 500);
    
    
    useEffect(() => {
        if (userId && currentView === CURRENT_VIEW.CHAT_VIEW) {
            getAllChats();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [userId, search, currentView, isChatOpen, chatNotifications]);

    return (
        <div className='floating-chat-container'>
            {!isChatOpen && (
                <span>
                    <Button
                        className={`floating-chat-btn`}
                        onClick={() => dispatch(toggleChatOpen())}
                        title='Chat With Users'
                    >
                        <ChatIcon/>
                        {chatNotifications > 0 && (
                            <span
                                className='d-flex justify-content-center position-absolute top-0 start-100 translate-middle badge rounded-pill bg-danger align-items-center'
                                style={{ fontSize: '11px', width: '22px', height: '22px' }}
                            >
                                {chatNotifications > 99 ? '+99' : chatNotifications}
                                <span className='visually-hidden'>unread messages</span>
                            </span>
                        )}
                    </Button>
                </span>
            )}
            {isChatOpen && (
                <div className={`chat-panel ${isChatOpen ? 'open' : ''}`}>
                    <ChatHeader
                        currentView={currentView}
                        currentUser={selectedUser}
                        setSearch={setSearch}
                        search={search}
                        setMessageSearch={setMessageSearch}
                    />
                    {currentView === CURRENT_VIEW.MESSAGE_VIEW && (
                        <ChatMessageBody messageSearch={messageSearch} />
                    )}
                    {currentView === CURRENT_VIEW.CHAT_VIEW && (
                        <>
                            <ChatSearch
                                search={search}
                                setSearch={setSearch}
                                placeholder='Search Chat...'
                            />
                            <div className='chat-body'>
                                {chatList?.length > 0 ? (
                                    <ChatList
                                        className='chat-list'
                                        dataSource={chatList}
                                        onClick={(item) => {
                                            dispatch(setSelectedUser(item));
                                            dispatch(setCurrentView(CURRENT_VIEW.MESSAGE_VIEW));
                                        }}
                                    />
                                ) : (
                                    <NoDataFound
                                        heading='No Chats Found'
                                        message='Let user start a conversation'
                                    />
                                )}
                            </div>
                        </>
                    )}
                    {currentView === CURRENT_VIEW.MESSAGE_VIEW && !isCsmCaseload && <ChatFooter />}
                    {currentView === CURRENT_VIEW.NEW_CHAT_VIEW && !isCsmCaseload && (
                        <>
                            <ChatSearch
                                search={chatSearch}
                                setSearch={setChatSearch}
                                placeholder='Search User...'
                            />
                            <NewChatView managementId={userId} search={chatSearch} />
                        </>
                    )}
                    {currentView === CURRENT_VIEW.CHAT_VIEW && !isCsmCaseload && (
                        <div className='chat-search-footer'>
                            <span id='action-icon' className='border-on-hover' title='New Chat'>
                                <button
                                    type='button'
                                    className='ssc-secondary-white-btn'
                                    onClick={() =>
                                        dispatch(setCurrentView(CURRENT_VIEW.NEW_CHAT_VIEW))
                                    }
                                >
                                    New Chat
                                </button>
                            </span>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};
